import { useViewThread } from "@hooks/threads/useViewThread";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { useEffect, useRef } from "react";
import { useThread } from "../hooks/useThread";
import { CurrentViewers } from "./CurrentViewers";
import { MessageContainer } from "./MessageContainer";
import { ReadReceipt } from "./ReadReceipt";
import { ReplyBox } from "./ReplyBox";
import styles from "./style.module.css";
import { ThreadSummary } from "./ThreadSummary";

export function ThreadContents() {
  const { features, permissions } = useUserConfiguration();

  const {
    thread,
    groupedMessages,
    lastReadStaffMessageIndex,
    threadSummary,
    isThreadSummaryPending,
  } = useThread();
  useViewThread(thread.id);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    // scroll to bottom on page load, or on new messages when the user is pretty close to the bottom
    const container = scrollContainerRef.current;
    if (!container || !bottomRef.current) return;

    if (isFirstLoad.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
      isFirstLoad.current = false;
    } else {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (distanceFromBottom < 600) {
        bottomRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [groupedMessages]);

  return (
    <div className={styles.threadContents}>
      {features.includes("secure_messages_ai") &&
        features.includes("secure_messages_redesign") &&
        (!!threadSummary || isThreadSummaryPending) && (
          <ThreadSummary summary={threadSummary?.body} loading={isThreadSummaryPending} />
        )}
      <div className={styles.messageContainerWrapper} ref={scrollContainerRef}>
        <div className={styles.messageChain}>
          {groupedMessages.map(({ message, files }, idx) => (
            <div key={message?.id || files[0].id}>
              <MessageContainer message={message} files={files} />
              {idx === lastReadStaffMessageIndex && (
                <ReadReceipt
                  timestamp={groupedMessages[lastReadStaffMessageIndex].message!.updated_at}
                />
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>
      <CurrentViewers threadId={thread.id} />
      {permissions.includes("indigo.add_message") ? (
        <div className={styles.replyBoxContainer}>
          <ReplyBox />
        </div>
      ) : (
        <div className="padding--bottom--xl" />
      )}
    </div>
  );
}
